/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * FRONTEND - API CLIENT
 * UPDATED: 2026-03-25
 * FIXES: [1] PATCH adicionado [2] TOKEN_KEY exportado [3] 401 sem loop
 *        [4] Redirect 401 por role [5] Refresh Token automático
 *        [6] API_BASE_URL corrigido para API_BASE
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { API_BASE } from '../utils/apiConfig';
export const TOKEN_KEY         = 'auth_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const USER_DATA_KEY     = 'user_data';

// ─── Tipos internos ───────────────────────────────────────────────────────────

interface UserData {
  role: 'admin' | 'affiliate' | 'student';
}

interface RefreshResponse {
  accessToken: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getRedirectByRole = (): string => {
  try {
    const raw = localStorage.getItem(USER_DATA_KEY);
    const user: UserData | null = raw ? JSON.parse(raw) : null;
    if (user?.role === 'admin')     return '/login-adm';
    if (user?.role === 'affiliate') return '/login-afiliado';
    return '/login-aluno';
  } catch {
    return '/login-aluno';
  }
};

const clearSession = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

// ─── ApiClient ────────────────────────────────────────────────────────────────

class ApiClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
  }> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE, // ✅ CORRIGIDO: era API_BASE_URL
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.setupInterceptors();
  }

  // ─── Processa fila de requisições enquanto refresh roda ─────────────────────

  private processQueue(error: unknown, token: string | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) prom.reject(error);
      else       prom.resolve(token!);
    });
    this.failedQueue = [];
  }

  // ─── Interceptors ────────────────────────────────────────────────────────────

  private setupInterceptors(): void {

    // REQUEST — injeta JWT em todo request
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error),
    );

    // RESPONSE — trata 401 com refresh automático
    this.instance.interceptors.response.use(
      (response) => response,

      async (error) => {
        const originalRequest = error.config;
        const isAuthRoute = originalRequest?.url?.includes('/auth/');

        // Ignora rotas de auth para evitar loop infinito
        if (error.response?.status !== 401 || isAuthRoute) {
          return Promise.reject(error);
        }

        // Se já está em refresh, enfileira a requisição
        if (this.isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.instance(originalRequest);
            })
            .catch(Promise.reject.bind(Promise));
        }

        // Primeira vez — tenta refresh
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

          if (refreshToken) {
            try {
              const { data } = await axios.post<RefreshResponse>(
                `${API_BASE}/auth/refresh`, // ✅ CORRIGIDO: era API_BASE_URL
                { refreshToken },
              );

              const newToken = data.accessToken;
              localStorage.setItem(TOKEN_KEY, newToken);
              this.instance.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              this.processQueue(null, newToken);
              this.isRefreshing = false;

              return this.instance(originalRequest); // re-tenta a requisição original
            } catch (refreshError) {
              this.processQueue(refreshError, null);
              this.isRefreshing = false;
              // Refresh falhou → logout
            }
          }
        }

        // Sem refresh ou refresh falhou → limpa sessão e redireciona
        const redirect = getRedirectByRole();
        clearSession();
        window.location.href = redirect;

        return Promise.reject(error);
      },
    );
  }

  // ─── Métodos HTTP ─────────────────────────────────────────────────────────────

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(url, data, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.patch(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, config);
    return response.data;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
export { API_BASE };